import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import FixedMenu from '../Common/Menus/FixedMenu.jsx';
import { loadJobsByCategory } from './utilities/escoAPI.js';

const JobsGallery = ({ onSelect, onClose, defaultCategory = '' }) => {
  const [category, setCategory] = useState(defaultCategory);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    setCategory(defaultCategory || '');
  }, [defaultCategory]);

  const trimmedCategory = useMemo(() => category.trim(), [category]);

  const handleSearch = async () => {
    if (!trimmedCategory) {
      setError('Please enter a category to search jobs.');
      setJobs([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await loadJobsByCategory(trimmedCategory, { limit: 40, includeDetails: true });
      setJobs(response?.results || []);
    } catch (err) {
      console.error('Error searching jobs:', err);
      setError('Failed to load jobs. Please try again.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (job) => {
    onSelect({
      ...job,
      category: trimmedCategory,
    });

    if (onClose) {
      onClose();
    }
  };

  return (
    <FixedMenu title="Select Desired Jobs" iconClass="bi-briefcase" expanded={true} setExpanded={onClose}>
      <div className="card-body p-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <input
              type="text"
              className="form-control"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Search by category, e.g. IT, finance, healthcare"
              disabled={loading}
            />
          </div>
          <div className="col-12 col-md-3">
            <button
              className="btn btn-primary w-100 h-100"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Searching
                </>
              ) : (
                <>
                  <i className="bi bi-search me-2"></i>
                  Search
                </>
              )}
            </button>
          </div>
        </div>

        {error ? (
          <div className="alert alert-danger mb-3">
            <small>{error}</small>
          </div>
        ) : null}

        {!loading && !error && jobs.length === 0 ? (
          <div className="alert alert-light border text-center mb-0">
            <small className="text-muted">Search a category to load jobs.</small>
          </div>
        ) : null}

        {jobs.length > 0 ? (
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">
              {jobs.length} job{jobs.length === 1 ? '' : 's'} found
            </small>
          </div>
        ) : null}

        <div className="row g-3">
          {jobs.map((job) => (
            <div key={job.id || job.uri || job.code || job.title} className="col-12 col-md-6 col-xl-4">
              <div className="card h-100">
                <div className="card-body d-flex flex-column">
                  <h6 className="card-title mb-2">{job.title || 'Untitled job'}</h6>
                  <p
                    className="card-text text-muted small flex-grow-1"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {job.description || 'No description available.'}
                  </p>
                  <div className="d-flex justify-content-between align-items-center mt-2 gap-2">
                    <span className="badge text-bg-light border text-truncate">
                      {job.type || 'occupation'}
                    </span>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleSelect(job)}
                    >
                      <i className="bi bi-plus-lg me-1"></i>
                      Select
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </FixedMenu>
  );
};

JobsGallery.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  defaultCategory: PropTypes.string,
};

export default JobsGallery;
